import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import SubscriptionBudgetAllocationCheck from "@/shared/components/subscription-budget-check/SubscriptionBudgetAllocationCheck";
import { API_BASE_URL } from "@/config/appConfig";
import { useSSE } from "@/helpers/hooks/useSSE";
import { ROUTES } from "@/helpers/routes/routePaths";

const ALLOWED_PATH = [ROUTES.THANK_YOU];
const ALLOWED_FROM = ["package_selection"];

type LocationState = {
  is_redirect?: boolean;
  stripe_checkout?: boolean;
  from?: string;
  sessionId?: string;
} | null;

type ProgressData = {
  eventType?: string;
  processed_percentage?: number;
};

function LocationSubscriptionSSE() {
  const { pathname, state } = useLocation();
  const locationState = state as LocationState;
  const isRedirect = locationState?.is_redirect;
  const isStripeCheckout = locationState?.stripe_checkout;
  const sessionId = ALLOWED_FROM.includes(locationState?.from || "")
    ? locationState?.sessionId
    : null;

  const isAllowedPath = ALLOWED_PATH.includes(pathname);

  const showLoader =
    isRedirect && isStripeCheckout && isAllowedPath && sessionId;

  const URL = useMemo(
    () =>
      showLoader ? `${API_BASE_URL}/events?session_id=${sessionId}` : null,
    [sessionId, showLoader]
  );

  const { messages, close } = useSSE(URL);

  const messageData = useMemo<ProgressData | null>(
    () => (typeof messages === "string" ? JSON.parse(messages) : null),
    [messages]
  );

  const [progressData, setProgressData] = useState<ProgressData | null>(null);

  const handleProgress = useCallback((data: ProgressData) => {
    setProgressData((p) =>
      JSON.stringify(p) !== JSON.stringify(data) ? data : p
    );
  }, []);

  const handleProgressEnd = useCallback(() => {
    setProgressData(null);
    close();
  }, [close]);

  // Initializing progress data only when conditions are met
  useEffect(() => {
    if (showLoader) {
      setProgressData({});
    }
  }, [showLoader]);

  useEffect(() => {
    if (!showLoader) return;

    if (messageData?.eventType === "started") {
      setProgressData({ processed_percentage: 0 });
    } else if (
      messageData?.eventType === "completed" ||
      messageData?.processed_percentage === 100
    ) {
      setProgressData(messageData);
      setTimeout(() => {
        handleProgressEnd();
      }, 1000);
    } else if (messageData?.eventType === "progress") {
      handleProgress(messageData);
    }

    return () => {
      // eventNames.forEach((eventName) => off(eventName));
    };
  }, [handleProgress, handleProgressEnd, messageData, showLoader]);

  if (!showLoader) {
    return null;
  }

  return progressData ? (
    <SubscriptionBudgetAllocationCheck
      progress={progressData?.processed_percentage || 0}
    />
  ) : null;
}

export default LocationSubscriptionSSE;
